import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Users, UserPlus, UserX, Trash2, User } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";

interface TripParticipantsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (participants: string[]) => void;
  initialParticipants?: string[];
}

export const TripParticipantsDialog = ({
  open,
  onOpenChange,
  onSave,
  initialParticipants = [],
}: TripParticipantsDialogProps) => {
  const [participants, setParticipants] = useState<string[]>(initialParticipants);
  const [numParticipants, setNumParticipants] = useState("");

  useEffect(() => {
    if (open && initialParticipants.length > 0) {
      setParticipants(initialParticipants);
    }
  }, [open, initialParticipants]);

  const handleQuickAdd = () => {
    const num = parseInt(numParticipants);
    if (num > 0) {
      const newParticipants = Array.from({ length: num }, (_, i) => `Participant ${participants.length + i + 1}`);
      setParticipants([...participants, ...newParticipants]);
      toast.success(`Added ${num} participants`);
    }
  };

  const handleAddParticipant = () => {
    setParticipants([...participants, ""]);
    toast.success("New participant field added");
  };

  const handleRemoveParticipant = (index: number) => {
    if (participants.length === 1) {
      toast.error("At least one participant is required");
      return;
    }
    const newParticipants = participants.filter((_, i) => i !== index);
    setParticipants(newParticipants);
    toast.success("Participant removed");
  };

  const handleParticipantChange = (index: number, value: string) => {
    const newParticipants = [...participants];
    newParticipants[index] = value;
    setParticipants(newParticipants);
  };

  const handleSave = () => {
    const validParticipants = participants.filter(p => p.trim() !== "");
    if (validParticipants.length === 0) {
      toast.error("Please add at least one participant");
      return;
    }

    const duplicates = validParticipants.filter((item, index) => validParticipants.indexOf(item) !== index);
    if (duplicates.length > 0) {
      toast.error("Duplicate participant names are not allowed");
      return;
    }

    onSave(validParticipants);
    onOpenChange(false);
    toast.success(`${validParticipants.length} participants saved`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Trip Participants
          </DialogTitle>
          <DialogDescription>
            Add the names of people participating in this trip.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-4">
          <Input
            type="number"
            placeholder="Number of participants"
            value={numParticipants}
            onChange={(e) => setNumParticipants(e.target.value)}
            min="1"
            className="w-48"
          />
          <Button onClick={handleQuickAdd} variant="secondary">
            Quick Add
          </Button>
        </div>

        <ScrollArea className="max-h-[400px] pr-4">
          <div className="space-y-4">
            <AnimatePresence mode="popLayout">
              {participants.map((participant, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center gap-2"
                >
                  <div className="relative flex-1 group">
                    <Input
                      placeholder={`Participant ${index + 1}`}
                      value={participant}
                      onChange={(e) => handleParticipantChange(index, e.target.value)}
                      className="pr-10 transition-all duration-200 border-2 focus:border-primary"
                    />
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                    >
                      <User className="h-4 w-4 text-muted-foreground" />
                    </motion.div>
                  </div>
                  <motion.div whileHover={{ scale: 1.1 }}>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => handleRemoveParticipant(index)}
                      disabled={participants.length === 1}
                      className="shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </motion.div>
                </motion.div>
              ))}
            </AnimatePresence>

            <div className="mt-4">
              <h4 className="text-sm font-medium mb-2">Current Participants:</h4>
              <div className="flex flex-wrap gap-2">
                {participants.filter(p => p.trim() !== "").map((participant, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="flex items-center gap-1 animate-fade-in-up"
                  >
                    <User className="h-3 w-3" />
                    {participant}
                    <button
                      onClick={() => handleRemoveParticipant(index)}
                      className="ml-1 hover:text-destructive"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <motion.div
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <Button
                variant="outline"
                className="w-full bg-gradient-to-r from-muted/50 to-muted hover:from-muted hover:to-muted/50 transition-all duration-300"
                onClick={handleAddParticipant}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Participant
              </Button>
            </motion.div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button 
            onClick={handleSave}
            className="bg-gradient-primary hover:opacity-90 transition-opacity"
          >
            Save Participants
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};